import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  VStack,
  Text,
  useDisclosure,
  Spinner,
  SimpleGrid,
  Box,
  Image,
  Flex,
  Textarea,
  Checkbox,
  Wrap,
  WrapItem,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { ArrowBackIcon, SearchIcon } from '@chakra-ui/icons';
import { useGetLoras } from 'queries';
import { useAppContext } from 'hooks';

interface Lora {
  name: string;
  title: string;
  alias?: string;
  file_path?: string;
  image_url: string;
  is_nsfw: boolean;
  metadata?: {
    [key: string]: any;
    ss_output_name?: string;
  };
  trigger_words?: string;
  default_scale?: number; 
}

interface LoraSelectorProps {
  onSelect: (loraString: string, loraData?: { path: string, scale: number, image:string, title:string }) => void;
  isFlux: boolean;
}

const LoraSelector: React.FC<LoraSelectorProps> = ({ onSelect, isFlux }) => {
  const { isOpen, onOpen, onClose: originalOnClose } = useDisclosure();
  const [showNSFW, setShowNSFW] = useState(false);
  const { data, isLoading, error } = useGetLoras({ 
    isFlux,
    nsfwFilter: showNSFW 
  });
  const [selectedLora, setSelectedLora] = useState<Lora | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [loraString, setLoraString] = useState<string>('');
  const [isLoraView, setIsLoraView] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const lorasPerPage = 4;

  const [paginatedLoras, setPaginatedLoras] = useState<Lora[]>([]);
  const [filteredLoras, setFilteredLoras] = useState<Lora[]>([]);

  useEffect(() => {
    if (data?.loras) {
      const filtered = showNSFW 
        ? data.loras 
        : data.loras.filter(lora => !lora.is_nsfw);
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchResults = filtered.filter(lora => 
          lora.name.toLowerCase().includes(searchLower) ||
          lora.title.toLowerCase().includes(searchLower) ||
          (lora.alias && lora.alias.toLowerCase().includes(searchLower))
        );
        setFilteredLoras(searchResults);
      } else {
        setFilteredLoras(filtered);
      }
      setCurrentPage(1);
    }
  }, [data, showNSFW, searchTerm]);

  useEffect(() => {
    const indexOfLastLora = currentPage * lorasPerPage;
    const indexOfFirstLora = indexOfLastLora - lorasPerPage;
    setPaginatedLoras(filteredLoras.slice(indexOfFirstLora, indexOfLastLora));
  }, [filteredLoras, currentPage]);

  useEffect(() => {
    if (selectedLora) {
      if (isFlux) {
        setSelectedKeywords([]); // Starts empty but doesn't override user selection
      } else {
        const keywords = selectedLora.trigger_words?.split(",").map(k => k.trim()) || [];
        setSelectedKeywords(keywords);
      }
    }
  }, [selectedLora, isFlux]);
  
  
  useEffect(() => {
    if (selectedLora) {
      if (isFlux) {
        setLoraString(selectedKeywords.join(", ")); // Only selected words
      } else {
        const loraName = selectedLora.alias || selectedLora.name;
        const keywordsString = selectedKeywords.length > 0 ? ` ${selectedKeywords.join(', ')}` : '';
        setLoraString(`<lora:${loraName}:0.8>${keywordsString}`);
      }
    }
  }, [selectedLora, selectedKeywords, isFlux]);
  

const handleSelect = () => {
  if (selectedLora) {
    console.log("Selected Lora in handleSelect:", selectedLora);
    if (isFlux && selectedLora.file_path) {
      onSelect(
        selectedKeywords.join(", "),  // ✅ Now only selected trigger words are sent
        {
          path: selectedLora.file_path,
          scale: selectedLora.default_scale || 0.5,
          image: selectedLora.image_url,
          title: selectedLora.title
        }
      );
    }
     else if (!isFlux && loraString) {
      onSelect(loraString);
    }
    onClose();
    originalOnClose();
  }
};

  const onClose = () => {
    if (!isLoraView && selectedLora) {
      setIsLoraView(true);
      setSelectedLora(null);
    } else {
      originalOnClose();
    }
  };

  const handleLoraClick = (lora: Lora) => {
    console.log("Selected Lora in handleLoraClick:", lora);
    setSelectedLora(lora);
    setSelectedKeywords([]);
    setIsLoraView(false);
  };

  const handleKeywordToggle = (keyword: string) => {
    setSelectedKeywords(prev =>
      prev.includes(keyword)
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const renderKeywords = () => {
    const keywords = selectedLora?.trigger_words?.split(',').map(k => k.trim()) || [];


    
    return (
      <Flex align="start" direction="column">
        {keywords.length > 0 && (
          <>
            <Text fontWeight="bold" color="#FF1B6D" fontSize="20px">Activation Text</Text>
            <Text>Will be added to prompt along with Lora</Text>
          </>
        )}
        <Wrap mt="20px" spacing={1}>
          {keywords.map((keyword, index) => (
            <WrapItem key={index}>
              <Checkbox
                value={keyword}
                isChecked={selectedKeywords.includes(keyword)}
                onChange={() => handleKeywordToggle(keyword)}
                sx={{
                  'span.chakra-checkbox__control': {
                    display: 'none',
                  },
                  'span.chakra-checkbox__label': {
                    border: '2px solid gray',
                    borderRadius: '32px',
                    padding: '4px 12px',
                    transition: 'all 0.2s',
                  },
                  '&[data-checked] span.chakra-checkbox__label': {
                    backgroundColor: 'blue.500',
                    color: 'white',
                    borderColor: 'blue.500',
                  },
                }}
              >
                {keyword}
              </Checkbox>
            </WrapItem>
          ))}
        </Wrap>
      </Flex>
    );
  };

  const totalPages = Math.ceil(filteredLoras.length / lorasPerPage);

  return (
    <>
      <Button w="100%" _hover={{opacity: 0.8}} borderRadius="12px" color="white" mt="15px" bg="#0E1118" border="1px solid white" onClick={onOpen}>+ Lora / Style</Button>

      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent 
          px={isLoraView ? {base:"10px", md: "10%", lg: "20%"} : "0"} 
          bg="#0A0E1D" 
          height="100vh"
        >
          {isLoraView && (
            <>
              <ModalHeader
                textAlign="center"
                fontSize={{base:"4xl", md:"8xl"}}
                fontWeight="bold"
                bgGradient="linear(to-r, #FFF0E1 34%, #A4DBEE 100%)"
                bgClip="text"
              >
                Add LoRa
              </ModalHeader>
              <Text fontSize={{base:"14px", md:"16px"}} textAlign="center">
                Insert Styles or action to your prompts.
              </Text>
              <Text fontSize={{base:"14px", md:"16px"}} textAlign="center">
                Want to know how to work with Lora? Check out our <span><a href="https://deepmode.com/blog/using-loras-on-deepmode-ai" style={{textDecoration: "underline"}} target="_blank">Lora guide</a></span>
              </Text>
              <Flex justify="center" align="center" mt={4}>
                <Text mr={2} fontSize="sm" color="gray.300">Show NSFW</Text>
                <Switch
                  isChecked={showNSFW}
                  onChange={(e) => setShowNSFW(e.target.checked)}
                  colorScheme="pink"
                />
              </Flex>
             
              <Box mt={4} px={4}>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search Loras..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    bg="white"
                    color="black"
                  />
                </InputGroup>
              </Box>
            </>
          )}
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Center>
              <Spinner />
              </Center>
            ) : error ? (
              <Text color="red.500">Error loading LoRAs. Please try again.</Text>
            ) : !isLoraView && selectedLora ? (
              <Flex h="100%" direction="column" align="center" justify="center">
                <Flex  direction="column" maxWidth="880px" width="100%" px={4}>
                  <Button 
                    maxW="130px"
                    onClick={() => {
                      setIsLoraView(true);
                      setSelectedLora(null);
                    }}
                    bg="transparent"
                    color="white"
                    border="1px solid white"
                    borderRadius="12px"
                    alignSelf="flex-start"
                    mb={4}
                  >
                    Back to Loras
                  </Button>
                  <Text fontWeight="bold" color="#FF1B6D" mb={4}>
                    {selectedLora.title}
                  </Text>
                  <Flex w="100%" direction={{ base: 'column', md: 'row' }} justify="space-between" align="stretch">
                    <Flex direction="column" justify="space-between" width={{ base: '100%' }} maxW="560px">
                    <Textarea
                        value={selectedKeywords.join(", ")} // Show only selected keywords
                        readOnly
                        height="100px"
                        mb={4}
                      />

                      {renderKeywords()}
                      <Button mt="30px" color="white" bg="#FF1B6D" onClick={handleSelect} isDisabled={!selectedLora}>
                        Add to Prompt
                      </Button>
                    </Flex>
                      <Image
                        src={selectedLora.image_url}
                        alt={selectedLora.name}
                        objectFit="contain"
                        maxHeight="350px"
                        borderRadius="10px"
                        display={{base:"none", md:"block"}}
                      />
                  </Flex>
                </Flex>
              </Flex>
            ) : (
              <VStack w="100%" spacing={6} align="stretch">
                <SimpleGrid mt="30px" columns={{base: 2, sm: 2, md: 3, lg: 4}} spacing={6} width="100%">
                  {paginatedLoras.map((lora, index) => (
                    <Box
                      key={index}
                      position="relative"
                      height="0"
                      paddingBottom="133%" 
                      width="100%"
                      cursor="pointer"
                      onClick={() => handleLoraClick(lora)}
                      border={selectedLora === lora ? "2px solid blue" : "none"}
                      borderRadius="10px"
                      overflow="hidden"
                    >
                      <Image
                        src={lora.image_url}
                        alt={lora.name}
                        objectFit="cover"
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        
                      />
                      <Box
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        bg="rgba(0,0,0,0.7)"
                        p={2}
                      >
                        <Text
                          color="white"
                          fontWeight="bold"
                          fontSize="sm"
                          textAlign="center"
                        >
                          {lora.title}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>
                {filteredLoras.length === 0 && (
                  <Text color="white" textAlign="center">No Loras found matching your search.</Text>
                )}
                {totalPages > 1 && (
                  <Flex justify="center" align="center" mt={4}>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      isDisabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Text mx={3}>
                      Page {currentPage} of {totalPages}
                    </Text>
                    <Button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      isDisabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </Flex>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LoraSelector;